<template>
  <div
    class="layout"
    :class="[computedCharacterColorClass]"
  >
    <!-- 梵字・家紋 + 戒名 + 位表記 -->
    <div
      class="main"
      :class="{
        adjust: isSymbolAvailable,
        other: underCharacters.length,
      }"
    >
      <!-- 梵字・家紋 -->
      <div>
        <div
          v-if="isSymbolAvailable"
          class="symbol flex center"
        >
          <div class="box">
            <ImageResolver
              :src="computedSymbolSrc"
              :size="30"
              class="character"
            />
          </div>
        </div>
      </div>

      <!-- スペーサー -->
      <div />

      <!-- 戒名 -->
      <div class="buddhistNames">
        <div
          v-for="(column, columnIndex) in columns"
          :key="`buddhistName-${column.order}`"
          class="buddhistName flex align-center column justify-space-between"
          :class="{
            left: column.order === 2,
            right: column.order === 1,
          }"
        >
          <!-- 戒名 -->
          <TabletCharacterImage
            v-for="(character, index) in column.buddhistName"
            :key="`character-${character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedCharacterSize"
            @onClickCharacter="onClickCharacter({
              ...buddhistNamePayload,
              characterIndex: index,
              columnIndex: columnIndex,
              ...$event,
            })"
          />
        </div>
      </div>

      <!-- スペーサー (0px or 15px) -->
      <div />

      <!-- 位表記 -->
      <div class="underCharacters">
        <!-- 位表記 > 霊ごと -->
        <template v-if="underCharactersPlacement === 'EACH_COLUMN'">
          <div
            v-for="target in ['left', 'right']"
            :key="`under-character-${target}`"
            class="underCharacter flex center column"
            :class="{
              left: target === 'left',
              right: target === 'right',
            }"
          >
            <TabletCharacterImage
              v-for="(character, index) in underCharacters"
              :key="`under-character-${character}-${index}`"
              :char="character"
              :font="typography.font"
              :size="computedCharacterSize"
              @onClickCharacter="onClickCharacter({
                ...underCharactersPayload,
                characterIndex: index,
                columnIndex: null,
                ...$event,
              })"
            />
          </div>
        </template>

        <template v-if="underCharactersPlacement === 'CENTER'">
          <div class="underCharacter middle flex center column">
            <TabletCharacterImage
              v-for="(character, index) in underCharacters"
              :key="`under-character-${character}-${index}`"
              :char="character"
              :font="typography.font"
              :size="computedCharacterSize"
              @onClickCharacter="onClickCharacter({
                ...underCharactersPayload,
                characterIndex: index,
                columnIndex: null,
                ...$event,
              })"
            />
          </div>
        </template>
      </div>

      <!-- スペーサー -->
      <div />
    </div>

    <!-- 俗名 + 享年表記 + 没年齢 + 没年齢単位 -->
    <div
      class="side-extend"
      :class="{
        adjust: isSymbolAvailable,
      }"
    >
      <!-- スペーサー -->
      <div />

      <!-- 俗名の表記（ラベル） -->
      <div class="secularNamePrefixes">
        <!-- 俗名の表記（ラベル） > 霊ごと -->
        <div
          v-for="target in ['left', 'right']"
          :key="`secular-name-prefix-${target}`"
          class="secularNamePrefix flex center column"
          :class="{
            left: target === 'left',
            right: target === 'right',
          }"
        >
          <!-- 俗名の表記（ラベル） -->
          <TabletCharacterImage
            v-for="(character, index) in secularNamePrefix"
            :key="`character-${character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedSizeExtendCharacterSmallSize"
            :style="computedSizeExtendCharacterAdjustWidth"
            @onClickCharacter="onClickCharacter({
              ...secularNamePrefixPayload,
              characterIndex: index,
              columnIndex: null,
              ...$event,
            })"
          />
        </div>
      </div>

      <!-- 俗名 -->
      <div class="secularNames">
        <div
          v-for="(column, columnIndex) in columns"
          :key="`secularName-${column.order}`"
          class="secularName flex align-center column justify-space-between"
          :class="{
            left: column.order === 2,
            right: column.order === 1,
          }"
        >
          <!-- 俗名 -->
          <TabletCharacterImage
            v-for="(character, index) in column.secularName"
            :key="`character-${character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedSizeExtendCharacterSize"
            @onClickCharacter="onClickCharacter({
              ...secularNamePayload,
              characterIndex: index,
              columnIndex: columnIndex,
              ...$event,
            })"
          />
        </div>
      </div>

      <!-- スペーサー -->
      <div />

      <!-- 享年表示 -->
      <div class="ageAtDeathPrefixes">
        <!-- 享年表示 > 霊ごと -->
        <div
          v-for="(column, columnIndex) in columns"
          :key="`ageAtDeathPrefix-${column.order}`"
          class="ageAtDeathPrefix flex align-center column justify-space-between"
          :class="{
            left: column.order === 2,
            right: column.order === 1,
          }"
        >
          <!-- 享年表記 -->
          <TabletCharacterImage
            v-for="(character, index) in column.ageAtDeathPrefix"
            :key="`ageAtDeathPrefix-${character.character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedSizeExtendCharacterSmallSize"
            :style="computedSizeExtendCharacterAdjustWidth"
            @onClickCharacter="onClickCharacter({
              ...ageAtDeathPrefixPayload,
              characterIndex: index,
              columnIndex: columnIndex,
              ...$event,
            })"
          />
        </div>
      </div>

      <!-- 没年齢 -->
      <div class="ageAtDeaths">
        <div
          v-for="(column, columnIndex) in columns"
          :key="`ageAtDeath-${column.order}`"
          class="ageAtDeath flex align-center column justify-space-between"
          :class="{
            left: column.order === 2,
            right: column.order === 1,
          }"
        >
          <!-- 没年齢 -->
          <TabletCharacterImage
            v-for="(character, index) in column.ageAtDeath"
            :key="`ageAtDeath-${character.character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedSizeExtendCharacterSmallSize"
            :style="computedSizeExtendCharacterAdjustWidth"
            @onClickCharacter="onClickCharacter({
              ...ageAtDeathPayload,
              characterIndex: index,
              columnIndex: columnIndex,
              ...$event,
            })"
          />
        </div>
      </div>

      <!-- 没年齢単位 -->
      <div class="ageUnitCharacters">
        <div
          v-for="(column, columnIndex) in columns"
          :key="`ageUnitCharacter-${column.order}`"
          class="ageUnitCharacter flex align-center column justify-space-between"
          :class="{
            left: column.order === 2,
            right: column.order === 1,
          }"
        >
          <div class="box grow" />
          <!-- 没年齢単位 -->
          <TabletCharacterImage
            v-for="(character, index) in column.ageUnitCharacter"
            :key="`diedCharacter-${character.character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedSizeExtendCharacterSmallSize"
            :style="computedSizeExtendCharacterAdjustWidth"
            @onClickCharacter="onClickCharacter({
              ...ageUnitCharacterPayload,
              characterIndex: index,
              columnIndex: columnIndex,
              ...$event
            })"
          />
        </div>
      </div>
    </div>

    <!-- 没年月日 -->
    <div
      class="side"
      :class="{
        adjust: isSymbolAvailable,
      }"
    >
      <!-- スペーサー -->
      <div />

      <!-- 没年 + 没月 + 没日 -->
      <div class="dates">
        <div
          v-for="(column, columnIndex) in columns"
          :key="`date-${column.order}`"
          class="date flex align-center column justify-space-between"
          :class="{
            left: column.order === 2,
            right: column.order === 1,
          }"
        >
          <!-- 没年 -->
          <TabletCharacterImage
            v-for="(character, index) in column.dateOfDeathYear"
            :key="`deathYear-${character.character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedSideCharacterSize"
            @onClickCharacter="onClickCharacter({
              ...dateOfDeathYearPayload,
              characterIndex: index,
              columnIndex: columnIndex,
              ...$event,
            })"
          />

           <!-- 没月 -->
          <TabletCharacterImage
            v-for="(character, index) in column.dateOfDeathMonth"
            :key="`deathMonth-${character.character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedSideCharacterSize"
            @onClickCharacter="onClickCharacter({
              ...dateOfDeathMonthPayload,
              characterIndex: index,
              columnIndex: columnIndex,
              ...$event,
            })"
          />

          <!-- 没日 -->
          <TabletCharacterImage
            v-for="(character, index) in column.dateOfDeathDay"
            :key="`deathDay-${character.character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedSideCharacterSize"
            @onClickCharacter="onClickCharacter({
              ...dateOfDeathDayPayload,
              characterIndex: index,
              columnIndex: columnIndex,
              ...$event,
            })"
          />
        </div>
      </div>

      <!-- 没表記 -->
      <div class="diedCharacters">
        <div
          v-for="column in columns"
          :key="`diedCharacter-${column.order}`"
          class="diedCharacter flex align-center column justify-space-between"
          :class="{
            left: column.order === 2,
            right: column.order === 1,
          }"
        >
          <!-- 没表記 -->
          <TabletCharacterImage
            v-for="(character, index) in diedCharacter"
            :key="`diedCharacter-${character.character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedSideCharacterSize"
            @onClickCharacter="onClickCharacter({
              ...diedCharacterPayload,
              characterIndex: index,
              columnIndex: null,
              ...$event,
            })"
          />
        </div>
      </div>

      <!-- スペーサー -->
      <div />
    </div>

    <!-- 縁 -->
    <div class="rim" />
  </div>
</template>

<script>
import TabletPreviewMixin from "@/mixins/tabletPreview.mixin";

export default {
  mixins: [
    TabletPreviewMixin.front,
    TabletPreviewMixin.character,
  ],
  components: {
  },
  props: {
  },
  mounted () {},
  data () {
    return {
    };
  },
  computed: {
    // 梵字・家紋 + 戒名 + 位表記
    // の文字列の長さから要素一つあたりのサイズを算出
    computedCharacterSize () {
      const {
        isSymbolAvailable,
        columns,
        underCharacters,
      } = this;

      const defaultSize = 30;
      const gap = underCharacters.length ? 10 : 0;
      const maxHeight = !isSymbolAvailable ? 400 - 15 - 30 - 30 : 340 - 15 - 30 - gap;

      const rightCharacterLength = columns[0].buddhistName.length + underCharacters.length;
      const leftCharacterLength = columns[1].buddhistName.length + underCharacters.length;
      const characterLength = Math.max(rightCharacterLength, leftCharacterLength);
      const characterSize = maxHeight / characterLength;
      const size = Math.min(defaultSize, characterSize);

      return size;
    },

    // 俗名の表記（ラベル）+ 俗名 + 享年表記 + 没年齢 + 没年齢単位
    // の文字列の長さから要素一つあたりのサイズを算出
    computedSizeExtendCharacterSize () {
      const {
        isSymbolAvailable,
        ageAtDeathPrefixPlacement,
        columns,
        secularNamePrefix,
      } = this;

      const defaultSize = 20;
      const maxHeight = !isSymbolAvailable ? 400 - 10 * 5 - 10 - 15 - 30 : 320 - 10 * 5 - 15 - 30;

      const rightAgeUnitCharacterLength = columns[0].ageUnitCharacter ? columns[0].ageUnitCharacter.length : 0;
      const leftAgeUnitCharacterLength = columns[1].ageUnitCharacter ? columns[1].ageUnitCharacter.length : 0;
      const ageUnitCharacterLength = Math.max(rightAgeUnitCharacterLength, leftAgeUnitCharacterLength);
      const rightAgeDeathPrefix = columns[0].ageAtDeathPrefix ? columns[0].ageAtDeathPrefix.length : 0;
      const leftAgeDeathPrefix = columns[1].ageAtDeathPrefix ? columns[1].ageAtDeathPrefix.length : 0;
      const ageAtDeathPrefixLength = ageAtDeathPrefixPlacement === "EACH_COLUMN"
        ? rightAgeDeathPrefix
        : Math.max(rightAgeDeathPrefix, leftAgeDeathPrefix);
      const rightCharacterLength = columns[0].secularName.length + secularNamePrefix.length + ageAtDeathPrefixLength + columns[0].ageAtDeath.length + ageUnitCharacterLength;
      const leftCharacterLength = columns[1].secularName.length + secularNamePrefix.length + ageAtDeathPrefixLength + columns[1].ageAtDeath.length + ageUnitCharacterLength;
      const characterLength = Math.max(rightCharacterLength, leftCharacterLength);
      const characterSize = maxHeight / characterLength;
      const size = Math.min(defaultSize, characterSize);

      return size;
    },

    // 俗名の表記（ラベル, 享年表記, 没年齢, 没年齢単位
    // の文字一つあたりのサイズを算出
    computedSizeExtendCharacterSmallSize () {
      const {
        computedSizeExtendCharacterSize,
      } = this;

      return computedSizeExtendCharacterSize * 0.75;
    },

    // 俗名の表記（ラベル, 享年表記, 没年齢, 没年齢単位
    // の文字一つあたりの幅サイズを算出
    computedSizeExtendCharacterAdjustWidth () {
      const {
        computedSizeExtendCharacterSize,
      } = this;

      const width = `${computedSizeExtendCharacterSize}px !important`;

      return {
        width,
      };
    },

    // 没年月日 + 没表記
    // の文字列の長さから要素一つあたりのサイズを算出
    computedSideCharacterSize () {
      const {
        isSymbolAvailable,
        columns,
        diedCharacter,
      } = this;

      const defaultSize = 15;
      const maxHeight = !isSymbolAvailable ? 400 - 30 - 30 : 320 - 30 - 30 - 15;

      const rightDateOfDeathLength = columns[0].dateOfDeath.length || 0;
      const leftDateOfDeathLength = columns[1].dateOfDeath.length || 0;
      const rightCharacterLength = rightDateOfDeathLength + diedCharacter.length;
      const leftCharacterLength = leftDateOfDeathLength + diedCharacter.length;
      const characterLength = Math.max(rightCharacterLength, leftCharacterLength);
      const characterSize = maxHeight / characterLength;
      const size = Math.min(defaultSize, characterSize);

      return size;
    },
  },
  watch: {},
  destroyed () {},
  methods: {},
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/variables.scss";
@import "~@/assets/styles/tablet-front.scss";

.layout {
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  .main {
    display: grid;
    grid-template-rows: auto 1fr auto 0px auto 2fr;
    grid-template-columns: 1fr;
    height: 100%;
    width: 80px;
    position: absolute;
    top: 0;
    left: -80px;
    padding-top: 15px; // 15(= rim)
    padding-bottom: 15px; // 15(= rim)
    box-sizing: border-box;
    overflow-x: visible;
    z-index: 1;

    &.adjust {
      padding-top: 15px; // 15(= rim)
      padding-bottom: 30px; // (80 - 50) / 2 + 15(= rim)
    }

    &.other {
      grid-template-rows: auto 2fr auto 10px auto 2fr;
    }

    @include symbolWithSizing(190px, 60px, 30px); // (300 + 80) / 2

    .buddhistNames {
      height: 100%;
      width: 100%;
      position: relative;
      box-sizing: border-box;

      .buddhistName {
        height: 100%;
        width: 30px;

        &.left {
          float: left;
          transform: translateX(195px);
        }

        &.right {
          float: right;
          transform: translateX(185px);
        }

        @include character(25px);
      }
    }

    .underCharacters {
      height: 100%;
      width: 100%;
      position: relative;
      box-sizing: border-box;

      .underCharacter {
        height: 100%;
        width: 30px;

        &.middle {
          transform: translateX(215px);
        }

        &.left {
          float: left;
          transform: translateX(195px);
        }

        &.right {
          float: right;
          transform: translateX(185px);
        }

        @include character(25px);
      }
    }
  }

  .side-extend {
    display: grid;
    grid-template-rows: 3fr auto auto 1fr auto auto auto;
    grid-template-columns: 1fr;
    gap: 10px;
    height: 100%;
    width: 60px;
    position: absolute;
    top: 0;
    left: 0px;
    padding-top: 30px;
    padding-bottom: 45px;
    box-sizing: border-box;
    overflow-x: visible;
    z-index: 2;

    &.adjust {
      height: calc(100% - 80px);
      top: 80px;
      grid-template-rows: 3fr auto auto  1fr auto auto auto;
      gap: 10px;
      padding-top: 30px;
      padding-bottom: 45px; // (80 - 50) / 2
    }

    @include symbol(105px); // (150 + 60) / 2

    .secularNamePrefixes {
      height: 100%;
      width: 100%;
      position: relative;
      box-sizing: border-box;

      .secularNamePrefix {
        height: 100%;

        &.left {
          float: left;
          transform: translateX(84px);
        }

        &.right {
          float: right;
          transform: translateX(156px);
        }

        @include character(25px);
      }
    }

    .secularNames {
      height: 100%;
      width: 100%;
      position: relative;
      box-sizing: border-box;

      .secularName {
        height: 100%;

        &.left {
          float: left;
          transform: translateX(84px);
        }

        &.right {
          float: right;
          transform: translateX(156px);
       }

        @include character(25px);
      }
    }

    .ageAtDeathPrefixes,
    .ageAtDeaths,
    .ageUnitCharacters {
      height: 100%;
      width: 100%;
      position: relative;
      box-sizing: border-box;

      .ageAtDeathPrefix,
      .ageAtDeath,
      .ageUnitCharacter {
        height: 100%;

        &.left {
          float: left;
          transform: translateX(84px);
        }

        &.right {
          float: right;
          transform: translateX(156px);
        }

        @include character(25px);
      }
    }
  }

  .side {
    display: grid;
    grid-template-rows: 1fr auto auto 1fr;
    grid-template-columns: 1fr;
    height: 100%;
    position: absolute;
    top: 0;
    left: -100px;
    box-sizing: border-box;
    padding: 15px 2.5px;
    z-index: 3;

    &.adjust {
      height: calc(100% - 80px);
      top: 80px;
      padding-bottom: 30px;
    }

    .dates,
    .diedCharacters {
      height: 100%;
      width: 100%;
      position: relative;

      &.adjust {
        padding-top: 15px;
      }

      .date,
      .diedCharacter {
        height: 100%;
        width: 20px;

        &.left {
          float: left;
          transform: translateX(149px);
        }

        &.right {
          float: right;
          transform: translateX(306px);
        }

        @include character(15px);
      }
    }
  }

  .rim {
    display: block;
    height: 376px;
    width: 276px;
    margin: 12px;
    position: relative;
    box-sizing: border-box;
    border: 3px solid white;
  }
}
</style>
